//src/pages/LoginPage.tsx
import { useState } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, Loader2, Moon, Sun, Mail } from 'lucide-react';
import { toast } from 'sonner';
import loginHero from '@/assets/login-hero.jpg';

function WhatsAppIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 32 32"
      fill="currentColor"
      aria-hidden="true"
      className={className}
    >
      <path d="M19.11 17.21c-.27-.14-1.6-.79-1.84-.88-.25-.09-.43-.14-.61.14-.18.27-.7.88-.86 1.06-.16.18-.32.2-.59.07-.27-.14-1.14-.42-2.17-1.34-.8-.71-1.34-1.58-1.5-1.85-.16-.27-.02-.41.12-.55.12-.12.27-.32.41-.48.14-.16.18-.27.27-.46.09-.18.05-.34-.02-.48-.07-.14-.61-1.47-.84-2.02-.22-.53-.45-.46-.61-.47h-.52c-.18 0-.48.07-.73.34-.25.27-.95.93-.95 2.27s.98 2.63 1.11 2.82c.14.18 1.92 2.92 4.64 4.09.65.28 1.16.45 1.56.58.66.21 1.26.18 1.73.11.53-.08 1.6-.65 1.82-1.28.23-.63.23-1.17.16-1.28-.06-.11-.24-.18-.5-.32Z" />
      <path d="M16.02 3.2c-7.07 0-12.8 5.72-12.8 12.77 0 2.25.59 4.45 1.71 6.38L3.1 28.8l6.63-1.74a12.84 12.84 0 0 0 6.28 1.61h.01c7.06 0 12.79-5.72 12.79-12.77 0-3.41-1.33-6.62-3.75-9.03A12.72 12.72 0 0 0 16.02 3.2Zm0 23.3h-.01a10.68 10.68 0 0 1-5.44-1.49l-.39-.23-3.93 1.03 1.05-3.83-.25-.4a10.57 10.57 0 0 1-1.63-5.61c0-5.87 4.77-10.65 10.62-10.65 2.83 0 5.49 1.1 7.49 3.1a10.55 10.55 0 0 1 3.11 7.55c0 5.87-4.77 10.64-10.62 10.64Z" />
    </svg>
  );
}

export default function LoginPage() {
  const { login, isAuthenticated, isLoading: authLoading, isContextSelectionRequired } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const isDark = theme === 'dark';
  const logoSrc = isDark ? '/logo-v3-branco.png' : '/logo-v3-preto.png';

  if (authLoading) return null;
  if (isAuthenticated) return <Navigate to="/" replace />;
  if (isContextSelectionRequired) return <Navigate to="/selecionar-acesso" replace />;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error('Preencha e-mail e senha');
      return;
    }

    setIsLoading(true);
    try {
      const result = await login({ email, password });
      if (result.contextSelectionRequired) {
        navigate('/selecionar-acesso');
        return;
      }
      toast.success('Login realizado com sucesso!');
      navigate('/');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha no login');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleTheme}
        className="absolute top-4 right-4 z-10"
      >
        {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </Button>

      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden">
        <img
          src={loginHero}
          alt="Profissional em ambiente corporativo"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="absolute top-0 right-0 w-2 h-full bg-primary" />

        <div className="relative z-10 flex flex-col justify-end p-10 pb-12 text-white">
          <span className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground text-sm font-semibold px-4 py-1.5 rounded-md w-fit mb-4">
            Cliente Normex
          </span>

          <h2 className="text-2xl md:text-3xl font-bold leading-tight mb-5">
            Estamos por aqui para te ajudar.
          </h2>

          <div className="flex flex-col gap-2 text-sm text-white/90">
            <a
              href="mailto:normex@normex.com.br"
              className="flex items-center gap-2 transition-colors hover:text-white"
            >
              <Mail className="h-4 w-4" />
              normex@normex.com.br
            </a>

            <a
              href="https://wa.me/555131913190"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 transition-colors hover:text-white"
              aria-label="Este número é somente WhatsApp"
            >
              <WhatsAppIcon className="h-4 w-4" />
              WhatsApp: +55 51 3191-3190
            </a>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center bg-background px-6 py-12">
        <div className="w-full max-w-sm space-y-8">
          <div>
            <div className="mb-6">
              <img
                src={logoSrc}
                alt="Normex"
                className="h-10 w-auto"
              />
            </div>
            <h1 className="text-xl font-semibold text-foreground">Acesse sua conta</h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                E-mail
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="nome@exemplo.com.br"
                value={email}
                onChange={e => setEmail(e.target.value)}
                autoComplete="email"
                required
                className="h-11"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Senha
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Senha"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  className="h-11 pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <Link
              to="/forgot-password"
              className="inline-block text-sm text-primary hover:underline"
            >
              Esqueceu sua senha?
            </Link>

            <Button type="submit" className="w-full h-11" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Entrar
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
